import React, { useRef, useState, useEffect } from "react";
import PortalDropdown from "./PortalDropdown";

/**
 * Text input backed by the saved Locations database.
 *
 * Props:
 *   value             – current text value (may contain "\n" for multi-line addresses)
 *   onChange(str)     – called when the committed value changes (pick or clear)
 *   onPick(location)  – (optional) called when the user picks a suggestion
 *   locations         – array of location objects
 *   placeholder, className, dataTestid, disabled – passthrough props
 *   multiline         – when true, the search field is a textarea
 *   rows              – textarea rows (default 3)
 *   strict            – when true, the user MUST pick from the saved locations list
 *   maxChars          – when > 0, truncates the chip display to this many characters
 */
export default function LocationAutocomplete({
  value = "",
  onChange,
  onPick,
  locations = [],
  placeholder = "",
  className = "",
  dataTestid,
  disabled = false,
  multiline = false,
  rows = 3,
  strict = false,
  maxChars = 0,
}) {
  const [focused, setFocused] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setFocused(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const q = ((strict ? search : value) || "").trim().toLowerCase();
  const matches = (locations || []).filter((l) => {
    if (!q) return false;
    return (
      (l.name || "").toLowerCase().includes(q) ||
      (l.location_name || "").toLowerCase().includes(q) ||
      (l.location_address || "").toLowerCase().includes(q)
    );
  }).slice(0, 8);

  const pick = (l) => {
    const addr = l.location_address || l.location_name || "";
    const nm = (l.name || "").trim();
    let combined;
    if (nm && addr && nm !== addr) combined = `${nm}\n${addr}`;
    else combined = addr || nm;
    onChange && onChange(combined);
    onPick && onPick(l);
    setSearch("");
    setFocused(false);
  };

  const clear = () => {
    onChange && onChange("");
    setSearch("");
    setFocused(false);
  };

  const hasValue = !!(value && String(value).trim());

  // ── STRICT MODE + HAS VALUE + NOT FOCUSED → read-only chip ──
  if (strict && hasValue && !focused) {
    const lines = String(value).split("\n").filter((l, i, a) => l || i < a.length - 1);
    const [head, ...rest] = lines;
    const displayHead = maxChars > 0 && head && head.length > maxChars
      ? head.slice(0, maxChars) + "…"
      : head;
    const showAddress = maxChars === 0;
    return (
      <div ref={wrapRef} className="relative w-full">
        <div
          data-testid={dataTestid}
          onClick={() => { if (!disabled) { setFocused(true); setSearch(""); setTimeout(() => inputRef.current && inputRef.current.focus(), 0); } }}
          className={className + " cursor-text flex items-start justify-between gap-2"}
          title={String(value)}
        >
          <div className="flex-1 min-w-0 leading-tight">
            <div className="font-bold truncate">{displayHead}</div>
            {showAddress && rest.map((line, i) => (
              <div key={i} className="text-[11px] opacity-80 truncate">{line}</div>
            ))}
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); clear(); }}
              className="opacity-40 hover:opacity-100 hover:text-red-400 text-xs px-1 self-start"
              title="Clear location"
              data-testid={dataTestid ? `${dataTestid}-clear` : undefined}
            >✕</button>
          )}
        </div>
      </div>
    );
  }

  // ── SEARCH / EMPTY MODE ──
  const searchValue = strict ? search : value;
  const handleInputChange = (e) => {
    if (strict) setSearch(e.target.value);
    else onChange && onChange(e.target.value);
  };
  const commonInputProps = {
    ref: inputRef,
    value: searchValue,
    onChange: handleInputChange,
    onFocus: () => setFocused(true),
    placeholder: strict ? (placeholder || "Type to search saved locations…") : placeholder,
    disabled,
    "data-testid": dataTestid,
    className,
    autoComplete: "off",
    onKeyDown: (e) => {
      if (e.key === "Enter" && matches.length > 0) {
        if (multiline && !(e.ctrlKey || e.metaKey) && !strict) return;
        e.preventDefault();
        pick(matches[0]);
      } else if (e.key === "Escape") {
        setFocused(false);
        setSearch("");
        e.target.blur && e.target.blur();
      }
    },
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      {multiline
        ? <textarea rows={rows} {...commonInputProps} />
        : <input type="text" {...commonInputProps} />}
      <PortalDropdown
        anchorRef={wrapRef}
        open={focused}
        maxHeight={256}
        className="bg-[#14181F] border border-[#28303F] rounded-lg shadow-2xl overflow-y-auto"
      >
        <div data-testid="location-autocomplete-suggestions">
          {matches.length > 0 ? (
            matches.map((l) => (
              <button
                key={l.id}
                type="button"
                data-testid="location-autocomplete-item"
                onMouseDown={(e) => { e.preventDefault(); pick(l); }}
                className="w-full flex items-start justify-between px-3 py-2 hover:bg-[#1A1F29] text-left transition-colors"
              >
                <div className="min-w-0">
                  <p className="text-xs text-white font-medium truncate">{l.name}</p>
                  {(l.location_address || l.location_name) && (
                    <p className="text-[10px] text-gray-500 truncate">{l.location_address || l.location_name}</p>
                  )}
                </div>
              </button>
            ))
          ) : strict ? (
            <div className="px-3 py-3 text-center text-[11px] text-gray-500 leading-relaxed">
              {searchValue ? (
                <>No matches. Add this location in the <a href="/locations" className="text-amber-400 hover:underline">Locations Database</a> first.</>
              ) : (
                <>Start typing to search your saved locations, or add new ones in the <a href="/locations" className="text-amber-400 hover:underline">Locations Database</a>.</>
              )}
            </div>
          ) : null}
        </div>
      </PortalDropdown>
    </div>
  );
}
